import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import {
  DEFAULT_ISLAND_KEY,
  getIslandAreasJsonUrl,
  getIslandDefaultMapViewport,
  getIslandRegionColorMap,
  getIslandRegionMapGeoJsonUrl,
  getIslandRegionViewportMap,
  slugifyGeographyName,
} from 'alakai-aloha-shared';
import { getRuntimeSiteContext } from '../../lib/siteContext';

/* global google */

const FALLBACK_MAP_CENTER = Object.freeze({ lat: 20.9, lng: -157.8 });
const FALLBACK_MAP_ZOOM = 8;
const DEFAULT_BOUNDS_PADDING = 60;
const FOCUSED_BOUNDS_PADDING = 40;
const AREA_DETAIL_FALLBACK_ZOOM = Object.freeze({ mobile: 11.5, desktop: 12.5 });
const GOOGLE_MAP_LIBRARIES = ['places'];

const normalizeMapAreaName = (name = '') => String(name)
  .toLowerCase()
  .replace(/[ʻ'`]/g, '')
  .replace(/[āáàâä]/g, 'a')
  .replace(/[ēéèêë]/g, 'e')
  .replace(/[īíìîï]/g, 'i')
  .replace(/[ōóòôö]/g, 'o')
  .replace(/[ūúùûü]/g, 'u')
  .trim();

const RegionsMapLite = ({
  initialRegion,
  highlightArea,
  selectable = false,
  areasSelectable = true,
  onSelect,
  className = "",
  onRegionHover,
  selectedRegion,
  selectedArea,
  onMapReady,
  lodgings = [],
  selectedLodging,
  focusedRegionMode = false,
  focusedAreaMode = false,
  showSelectedAreaLabelOnly = false,
  islandKey,
}) => {
  const runtimeIslandKey = useMemo(() => getRuntimeSiteContext().island, []);
  const resolvedIslandKey = islandKey || runtimeIslandKey || DEFAULT_ISLAND_KEY;
  const { isLoaded: isGoogleMapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAP_LIBRARIES,
    mapIds: [process.env.REACT_APP_MAP_ID],
  });
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [areas, setAreas] = useState([]);
  const polygonsRef = useRef([]);
  const polygonsLoadedRef = useRef(false);
  const selectedPolygonRef = useRef(null);
  const regionLabelsRef = useRef([]);
  const areaLabelsRef = useRef([]);
  const markersRef = useRef([]);
  const lodgingMarkersRef = useRef([]);
  const selectedLodgingMarkerRef = useRef(null);
  const areasRef = useRef([]);
  const initializingRef = useRef(false);
  
  // Update areas ref when areas change
  useEffect(() => {
    areasRef.current = areas;
  }, [areas]);

  const REGION_COLOR = useMemo(() => getIslandRegionColorMap(resolvedIslandKey), [resolvedIslandKey]);
  const REGION_ZOOM_SETTINGS = useMemo(() => getIslandRegionViewportMap(resolvedIslandKey), [resolvedIslandKey]);
  const DEFAULT_MAP_VIEWPORT = useMemo(() => getIslandDefaultMapViewport(resolvedIslandKey), [resolvedIslandKey]);
  const regionsGeoJsonUrl = useMemo(() => getIslandRegionMapGeoJsonUrl(resolvedIslandKey), [resolvedIslandKey]);
  const areasJsonUrl = useMemo(() => getIslandAreasJsonUrl(resolvedIslandKey), [resolvedIslandKey]);

  const normalizeFetchedAreas = useCallback((rawAreas = []) => {
    const counts = rawAreas.reduce((acc, area) => {
      const baseName = slugifyGeographyName(area.name || area.displayName || '');
      acc[baseName] = (acc[baseName] || 0) + 1;
      return acc;
    }, {});

    return rawAreas.map((area) => {
      const baseName = slugifyGeographyName(area.name || area.displayName || '');
      const uniqueName = counts[baseName] > 1
        ? slugifyGeographyName(`${area.region || 'area'} ${baseName}`)
        : baseName;

      return {
        ...area,
        name: uniqueName,
      };
    });
  }, []);

  const getResponsiveZoom = useCallback((zoomConfig) => {
    if (typeof zoomConfig === 'number') {
      return zoomConfig;
    }

    if (!zoomConfig) {
      return null;
    }

    const isMobile = mapRef.current && mapRef.current.offsetWidth < 500;
    return zoomConfig[isMobile ? 'mobile' : 'desktop'] ?? null;
  }, []);

  const getValidAreas = useCallback((candidateAreas = areasRef.current) => (
    (candidateAreas || []).filter((area) =>
      Number.isFinite(Number(area?.lat)) && Number.isFinite(Number(area?.lng)))
  ), []);

  const getAreasForRegion = useCallback((regionName) => {
    const validAreas = getValidAreas(areasRef.current);
    if (!regionName) {
      return validAreas;
    }

    return validAreas.filter((area) => area.region === regionName);
  }, [getValidAreas]);

  const findMatchingArea = useCallback((areaName, regionName) => {
    const normalizedTarget = normalizeMapAreaName(areaName);
    if (!normalizedTarget) {
      return null;
    }

    return getValidAreas(areasRef.current).find((area) => {
      if (regionName && area.region !== regionName) {
        return false;
      }

      return (
        normalizeMapAreaName(area.displayName) === normalizedTarget ||
        normalizeMapAreaName(area.name) === normalizedTarget
      );
    }) || null;
  }, [getValidAreas]);

  const getBoundsForAreas = useCallback((candidateAreas) => {
    if (!window.google?.maps) return null;

    const bounds = new window.google.maps.LatLngBounds();
    let hasBounds = false;

    getValidAreas(candidateAreas).forEach((area) => {
      bounds.extend({ lat: Number(area.lat), lng: Number(area.lng) });
      hasBounds = true;
    });

    return hasBounds ? bounds : null;
  }, [getValidAreas]);

  const applyViewport = useCallback((mapInstance, viewport) => {
    if (!mapInstance || !viewport?.center) {
      return false;
    }

    const zoom = getResponsiveZoom(viewport.zoom);
    if (zoom == null) {
      return false;
    }

    mapInstance.setCenter(viewport.center);
    mapInstance.setZoom(zoom);
    return true;
  }, [getResponsiveZoom]);

  const fitToDefaultView = useCallback((mapInstance = map) => {
    if (!mapInstance || !window.google?.maps) return;

    if (applyViewport(mapInstance, DEFAULT_MAP_VIEWPORT)) {
      return;
    }

    const polygonBounds = new window.google.maps.LatLngBounds();
    let hasPolygonBounds = false;

    polygonsRef.current.forEach((polygon) => {
      const path = polygon.getPath && polygon.getPath();
      if (!path) return;

      path.getArray().forEach((latLng) => {
        polygonBounds.extend(latLng);
        hasPolygonBounds = true;
      });
    });

    if (hasPolygonBounds) {
      mapInstance.fitBounds(polygonBounds, { padding: DEFAULT_BOUNDS_PADDING });
      return;
    }

    const areaBounds = getBoundsForAreas(areasRef.current);
    if (areaBounds) {
      mapInstance.fitBounds(areaBounds, { padding: DEFAULT_BOUNDS_PADDING });
    }
  }, [DEFAULT_MAP_VIEWPORT, applyViewport, getBoundsForAreas, map]);

  const focusMapOnSelection = useCallback((mapInstance = map, overrides = {}) => {
    if (!mapInstance || !window.google?.maps) return false;

    const regionName = overrides.regionName ?? selectedRegion ?? null;
    const areaName = overrides.areaName ?? selectedArea ?? null;
    const allowAreaFallback = overrides.allowAreaFallback ?? focusedAreaMode;

    if (regionName) {
      const viewport = REGION_ZOOM_SETTINGS[regionName];
      if (applyViewport(mapInstance, viewport)) {
        return true;
      }

      const regionBounds = getBoundsForAreas(getAreasForRegion(regionName));
      if (regionBounds) {
        mapInstance.fitBounds(regionBounds, { padding: FOCUSED_BOUNDS_PADDING });
        return true;
      }

      if (allowAreaFallback && areaName) {
        const matchedArea = findMatchingArea(areaName, regionName);
        if (matchedArea) {
          mapInstance.setCenter({ lat: Number(matchedArea.lat), lng: Number(matchedArea.lng) });
          mapInstance.setZoom(getResponsiveZoom(AREA_DETAIL_FALLBACK_ZOOM) ?? FALLBACK_MAP_ZOOM);
          return true;
        }
      }
    }

    if (applyViewport(mapInstance, DEFAULT_MAP_VIEWPORT)) {
      return true;
    }

    const islandBounds = getBoundsForAreas(areasRef.current);
    if (islandBounds) {
      mapInstance.fitBounds(islandBounds, { padding: DEFAULT_BOUNDS_PADDING });
      return true;
    }

    return false;
  }, [
    DEFAULT_MAP_VIEWPORT,
    REGION_ZOOM_SETTINGS,
    applyViewport,
    findMatchingArea,
    focusedAreaMode,
    getAreasForRegion,
    getBoundsForAreas,
    getResponsiveZoom,
    map,
    selectedArea,
    selectedRegion,
  ]);

  // Simple manual zoom function
  const zoomToRegionByName = useCallback((name) => {
    if (!map) return;
    
    // If name is null, reset to default view  
    if (name === null) {
      // Reset all polygons to default style
      polygonsRef.current.forEach(poly => {
        poly.setOptions({ 
          fillOpacity: 0.15,
          strokeWeight: 1
        });
      });
      selectedPolygonRef.current = null;
      
      // Show region labels, hide area labels in default view
      regionLabelsRef.current.forEach(overlay => {
        overlay.setMap(map);
      });
      areaLabelsRef.current.forEach(overlay => {
        overlay.setMap(null);
      });
      
      fitToDefaultView(map);
      return;
    }

    focusMapOnSelection(map, {
      regionName: name,
      areaName: focusedAreaMode ? selectedArea : null,
      allowAreaFallback: focusedAreaMode,
    });
    
    const polygon = polygonsRef.current.find(poly => poly.regionName === name);
    if (polygon) {
      const settings = REGION_ZOOM_SETTINGS[name];
      if (!applyViewport(map, settings)) {
        const bounds = new google.maps.LatLngBounds();
        polygon.getPath().getArray().forEach(latLng => {
          bounds.extend(latLng);
        });
        map.fitBounds(bounds, { padding: 10 });
      }

      polygonsRef.current.forEach(poly => {
        poly.setOptions({
          fillOpacity: 0.15,
          strokeWeight: 1
        });
      });

      polygon.setOptions({
        fillOpacity: 0.35,
        strokeWeight: 2
      });
      selectedPolygonRef.current = polygon;

      regionLabelsRef.current.forEach(overlay => {
        overlay.setMap(null);
      });
      areaLabelsRef.current.forEach(overlay => {
        const area = overlay.areaInfo;
        if (area && area.region === name) {
          overlay.setMap(map);
        } else {
          overlay.setMap(null);
        }
      });
    }
  }, [REGION_ZOOM_SETTINGS, applyViewport, fitToDefaultView, focusMapOnSelection, focusedAreaMode, map, selectedArea]);

  // Load region polygons function
  const loadRegionPolygons = useCallback((mapInstance) => {
    if (polygonsLoadedRef.current) return;
    
    polygonsLoadedRef.current = true;

    fetch(regionsGeoJsonUrl)
      .then(response => response.json())
      .then(geojson => {
        // Polygons are created directly with google.maps.Polygon
        
        geojson.features.forEach(feature => {
          const regionName = feature.getProperty ? feature.getProperty('name') : feature.properties.name;
          const coordinates = feature.geometry.coordinates;
          
          // In focused region or area mode, only show the selected region
          if ((focusedRegionMode || focusedAreaMode) && selectedRegion && regionName !== selectedRegion) {
            return; // Skip this polygon
          }
          
          // Convert GeoJSON coordinates to Google Maps LatLng
          const paths = coordinates[0].map(coord => ({
            lat: coord[1],
            lng: coord[0]
          }));
          
          const polygon = new google.maps.Polygon({
            paths: paths,
            fillColor: (focusedRegionMode || focusedAreaMode) ? 'transparent' : (REGION_COLOR[regionName] || '#666'),
            fillOpacity: (focusedRegionMode || focusedAreaMode) ? 0 : 0.15,
            strokeColor: REGION_COLOR[regionName] || '#666',
            strokeWeight: (focusedRegionMode || focusedAreaMode) ? 2 : 1,
            clickable: (focusedRegionMode || focusedAreaMode) ? false : selectable,
            map: mapInstance
          });
          
          // Store polygon with region name for reference
          polygon.regionName = regionName;
          polygonsRef.current.push(polygon);
          
          // Add hover behavior (visual only, no card triggers) - only if selectable
          if (selectable) {
            polygon.addListener('mouseover', () => {
              if (selectedPolygonRef.current !== polygon) {
                polygon.setOptions({ fillOpacity: 0.25 });
              }
            });
            
            polygon.addListener('mouseout', () => {
              if (selectedPolygonRef.current !== polygon) {
                polygon.setOptions({ fillOpacity: 0.15 });
              }
            });
          }
          
          // Add click behavior - only if selectable
          if (selectable) {
            polygon.addListener('click', () => {
              // Reset all polygons to default style
              polygonsRef.current.forEach(poly => {
                poly.setOptions({ 
                  fillOpacity: 0.15,
                  strokeWeight: 1
                });
              });
              
              // Highlight selected polygon
              polygon.setOptions({ 
                fillOpacity: 0.35,
                strokeWeight: 2
              });
              selectedPolygonRef.current = polygon;
              
              // Zoom to polygon bounds with uniform padding
              const bounds = new google.maps.LatLngBounds();
              polygon.getPath().getArray().forEach(latLng => {
                bounds.extend(latLng);
              });
              const uniformPadding = 10; // Simple fallback for polygon clicks
              mapInstance.fitBounds(bounds, { padding: uniformPadding });
              
              // Notify parent
              if (onSelect) {
                onSelect('region', regionName);
              }
            });
          }

          // Create region label overlay (skip in focused region or area mode)
          if (!focusedRegionMode && !focusedAreaMode) {
            const labelBounds = new google.maps.LatLngBounds();
            polygon.getPath().getArray().forEach(latLng => {
              labelBounds.extend(latLng);
            });
            const center = labelBounds.getCenter();

          const labelDiv = document.createElement('div');
          labelDiv.className = 'region-label';
          labelDiv.style.cssText = `
            position: absolute;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 600;
            white-space: nowrap;
            pointer-events: none;
            transform: translate(-50%, -50%);
            z-index: 1000;
            border: 1px solid rgba(255, 255, 255, 0.3);
            backdrop-filter: blur(4px);
          `;
          labelDiv.textContent = regionName;

          const labelOverlay = new google.maps.OverlayView();
          labelOverlay.onAdd = function() {
            const panes = this.getPanes();
            panes.overlayMouseTarget.appendChild(labelDiv);
          };
          labelOverlay.draw = function() {
            const projection = this.getProjection();
            const position = projection.fromLatLngToDivPixel(center);
            labelDiv.style.left = position.x + 'px';
            labelDiv.style.top = position.y + 'px';
          };
          labelOverlay.onRemove = function() {
            if (labelDiv.parentNode) {
              labelDiv.parentNode.removeChild(labelDiv);
            }
          };
          labelOverlay.setMap(mapInstance);
          regionLabelsRef.current.push(labelOverlay);
          }
        });
        
        
        // Handle focused region or area mode after polygons are loaded
        if ((focusedRegionMode || focusedAreaMode) && selectedRegion) {
          zoomToRegionByName(selectedRegion);
        } else if (selectedRegion == null) {
          fitToDefaultView(mapInstance);
        }
      })
      .catch(err => {
        console.warn('Failed to load regions.geojson:', err);
        polygonsLoadedRef.current = false;
      });
  }, [fitToDefaultView, focusedAreaMode, focusedRegionMode, onSelect, regionsGeoJsonUrl, selectable, selectedRegion, zoomToRegionByName, REGION_COLOR]);

  // Load areas data
  useEffect(() => {
    fetch(areasJsonUrl)
      .then(res => res.json())
      .then(data => setAreas(normalizeFetchedAreas(data)))
      .catch(err => console.error('Failed to load areas:', err));
  }, [areasJsonUrl, normalizeFetchedAreas]);

  // Initialize map when areas are loaded
  useEffect(() => {
    if (!mapRef.current || !areas.length || initializingRef.current || map || !isGoogleMapsLoaded) return;

    initializingRef.current = true;
    let cancelled = false;

    const initializeMap = () => {
      try {
        if (!window.google || !window.google.maps) {
          throw new Error('Google Maps API failed to load');
        }

        if (cancelled) return;

        const mapInstance = new window.google.maps.Map(mapRef.current, {
          center:
            REGION_ZOOM_SETTINGS[selectedRegion]?.center ||
            ((focusedAreaMode && selectedArea)
              ? (() => {
                  const matchedArea = findMatchingArea(selectedArea, selectedRegion);
                  return matchedArea ? { lat: Number(matchedArea.lat), lng: Number(matchedArea.lng) } : null;
                })()
              : null) ||
            DEFAULT_MAP_VIEWPORT?.center ||
            (getValidAreas(areas)[0]
              ? { lat: Number(getValidAreas(areas)[0].lat), lng: Number(getValidAreas(areas)[0].lng) }
              : FALLBACK_MAP_CENTER),
          zoom:
            getResponsiveZoom(REGION_ZOOM_SETTINGS[selectedRegion]?.zoom) ??
            ((focusedAreaMode && selectedArea) ? getResponsiveZoom(AREA_DETAIL_FALLBACK_ZOOM) : null) ??
            getResponsiveZoom(DEFAULT_MAP_VIEWPORT?.zoom) ??
            FALLBACK_MAP_ZOOM,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          styles: [
            // Hide all default labels and POIs to reduce clutter
            {
              featureType: "poi",
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "transit",
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "administrative.locality",
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "administrative.neighborhood",
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "road.local",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            }
          ]
        });

        if (cancelled) return;

        setMap(mapInstance);
        initializingRef.current = false;

        areas.forEach(area => {
          const lat = Number(area?.lat);
          const lng = Number(area?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return;
          }

          // Check if this is the selected area in focused area mode
          const isSelectedArea = focusedAreaMode && selectedArea && area.displayName === selectedArea;

          // Create simple marker without label
          const marker = new window.google.maps.Marker({
            position: { lat, lng },
            map: mapInstance,
            title: area.displayName,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: isSelectedArea ? 10 : 6, // Bigger for selected area
              fillColor: REGION_COLOR[area.region] || '#666',
              fillOpacity: isSelectedArea ? 1.0 : 0.8, // More opaque for selected area
              strokeWeight: isSelectedArea ? 2 : 0, // Add stroke for selected area
              strokeColor: '#ffffff' // White stroke for selected area
            }
          });

          markersRef.current.push(marker);

          // Create custom area label overlay (initially hidden)
          const areaLabelDiv = document.createElement('div');
          areaLabelDiv.className = 'area-label';

          // Style differently for selected area in focused area mode
          if (isSelectedArea) {
            // Region-style label with dark background (like region labels)
            areaLabelDiv.style.cssText = `
              position: absolute;
              background: rgba(0, 0, 0, 0.7);
              color: white;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 14px;
              font-weight: 600;
              white-space: nowrap;
              pointer-events: none;
              transform: translate(-50%, -150%);
              z-index: 1000;
              border: 1px solid rgba(255, 255, 255, 0.3);
              backdrop-filter: blur(4px);
              cursor: default;
            `;
          } else {
            // Regular area label styling
            areaLabelDiv.style.cssText = `
              position: absolute;
              color: #000000;
              font-size: 15px;
              font-weight: 600;
              white-space: nowrap;
              pointer-events: ${selectable && areasSelectable ? 'auto' : 'none'};
              transform: translate(-50%, -150%);
              z-index: 5;
              cursor: ${selectable && areasSelectable ? 'pointer' : 'default'};
            `;
          }

          areaLabelDiv.textContent = area.displayName;

          // Add hover effect and click handler - but now make ALL areas clickable initially
          // We'll control clickability via CSS pointer-events instead
          if (selectable && areasSelectable) {
            // Add hover effect - subtle underline
            areaLabelDiv.addEventListener('mouseenter', () => {
              if (areaLabelDiv.style.pointerEvents !== 'none') {
                areaLabelDiv.style.textDecoration = 'underline';
              }
            });

            areaLabelDiv.addEventListener('mouseleave', () => {
              areaLabelDiv.style.textDecoration = 'none';
            });

            // Add click handler to ALL areas - let CSS pointer-events control actual clickability
            areaLabelDiv.addEventListener('click', (e) => {
              e.stopPropagation();
              if (onSelect) {
                onSelect('area', area.name);
              }
            });
          }

          const areaLabelOverlay = new window.google.maps.OverlayView();
          areaLabelOverlay.onAdd = function() {
            const panes = this.getPanes();
            // Use overlayMouseTarget to ensure click events work
            panes.overlayMouseTarget.appendChild(areaLabelDiv);
          };
          areaLabelOverlay.draw = function() {
            const projection = this.getProjection();
            const position = projection.fromLatLngToDivPixel({ lat, lng });
            areaLabelDiv.style.left = position.x + 'px';
            areaLabelDiv.style.top = position.y + 'px';
          };
          areaLabelOverlay.onRemove = function() {
            if (areaLabelDiv.parentNode) {
              areaLabelDiv.parentNode.removeChild(areaLabelDiv);
            }
          };

          // Store area info and label div reference for smart filtering and interactivity updates
          areaLabelOverlay.areaInfo = area;
          areaLabelOverlay.labelDiv = areaLabelDiv;
          areaLabelsRef.current.push(areaLabelOverlay);

          // Marker clicks are now handled by the label instead
        });

        // Only fitBounds if not in focused mode (let manual zoom take precedence)
        if (focusedRegionMode || focusedAreaMode) {
          focusMapOnSelection(mapInstance);
        } else {
          fitToDefaultView(mapInstance);
        }

        // Load region polygons
        loadRegionPolygons(mapInstance);

        if (onMapReady) {
          onMapReady({ zoomToRegionByName });
        }
      } catch (error) {
        console.error('[RegionsMapLite] Failed to initialize map', error);
        initializingRef.current = false;
        setMap(null);
      }
    };

    initializeMap();

    return () => {
      cancelled = true;
      initializingRef.current = false;
    };
  }, [DEFAULT_MAP_VIEWPORT, REGION_COLOR, REGION_ZOOM_SETTINGS, areas, areasSelectable, findMatchingArea, fitToDefaultView, focusMapOnSelection, focusedAreaMode, focusedRegionMode, getResponsiveZoom, getValidAreas, isGoogleMapsLoaded, loadRegionPolygons, map, onMapReady, onRegionHover, onSelect, selectable, selectedArea, selectedRegion, zoomToRegionByName]);

  // Handle selectedRegion changes
  useEffect(() => {
    if (!map) return;
    
    // In focused region or area mode, always show the selected region
    if ((focusedRegionMode || focusedAreaMode) && selectedRegion) {
      zoomToRegionByName(selectedRegion);
      return; // Skip normal logic in focused mode
    }
    
    // Normal mode logic
    if (selectedRegion === null) {
      // Reset to default view
      polygonsRef.current.forEach(poly => {
        poly.setOptions({ 
          fillOpacity: 0.15,
          strokeWeight: 1
        });
      });
      selectedPolygonRef.current = null;
      
      // Show region labels, hide area labels in default view
      regionLabelsRef.current.forEach(overlay => {
        overlay.setMap(map);
      });
      areaLabelsRef.current.forEach(overlay => {
        overlay.setMap(null);
      });
      
      fitToDefaultView(map);
    } else {
      zoomToRegionByName(selectedRegion);
    }
  }, [selectedRegion, selectedArea, map, fitToDefaultView, focusedRegionMode, focusedAreaMode, zoomToRegionByName]);

  // Update area label styling and markers when selectedArea changes in focusedAreaMode
  useEffect(() => {
    if (!map || !focusedAreaMode) return;

    // Update both area labels and markers based on selectedArea
    areaLabelsRef.current.forEach((overlay, index) => {
      const labelDiv = overlay.labelDiv;
      const area = overlay.areaInfo;
      const marker = markersRef.current[index];
      
      if (labelDiv && area && marker) {
        // Normalize comparison to handle Hawaiian diacritics
        const isSelectedArea = selectedArea && 
          (normalizeMapAreaName(area.displayName) === normalizeMapAreaName(selectedArea) ||
           normalizeMapAreaName(area.name) === normalizeMapAreaName(selectedArea));
        
        // Update marker styling
        marker.setOptions({
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: isSelectedArea ? 10 : 6, // Bigger for selected area
            fillColor: REGION_COLOR[area.region] || '#666',
            fillOpacity: isSelectedArea ? 1.0 : 0.8, // More opaque for selected area
            strokeWeight: isSelectedArea ? 2 : 0, // Add stroke for selected area
            strokeColor: '#ffffff' // White stroke for selected area
          }
        });
        
        // Update label styling by setting individual properties to preserve event handlers
        if (isSelectedArea) {
          if (showSelectedAreaLabelOnly) {
            overlay.setMap(map);
          }

          // Apply selected area styling (dark background like region labels)
          labelDiv.style.background = 'rgba(0, 0, 0, 0.7)';
          labelDiv.style.color = 'white';
          labelDiv.style.padding = '4px 8px';
          labelDiv.style.borderRadius = '4px';
          labelDiv.style.fontSize = '14px';
          labelDiv.style.border = '1px solid rgba(255, 255, 255, 0.3)';
          labelDiv.style.backdropFilter = 'blur(4px)';
          labelDiv.style.pointerEvents = 'none';
          labelDiv.style.cursor = 'default';
          labelDiv.style.zIndex = '1000';
        } else {
          if (showSelectedAreaLabelOnly) {
            overlay.setMap(null);
          }

          // Apply regular area label styling
          labelDiv.style.background = 'transparent';
          labelDiv.style.color = '#000000';
          labelDiv.style.padding = '0';
          labelDiv.style.borderRadius = '0';
          labelDiv.style.fontSize = '15px';
          labelDiv.style.border = 'none';
          labelDiv.style.backdropFilter = 'none';
          labelDiv.style.pointerEvents = selectable && areasSelectable ? 'auto' : 'none';
          labelDiv.style.cursor = selectable && areasSelectable ? 'pointer' : 'default';
          labelDiv.style.zIndex = '5';
        }
      }
    });
  }, [map, focusedAreaMode, selectedArea, selectable, areasSelectable, REGION_COLOR, showSelectedAreaLabelOnly]);

  // Update interactivity when selectable prop changes
  useEffect(() => {
    if (!map) return;

    // Update polygon clickability
    polygonsRef.current.forEach(polygon => {
      polygon.setOptions({ clickable: selectable });
    });

    // Update area label pointer events and cursor
    areaLabelsRef.current.forEach(overlay => {
      const labelDiv = overlay.labelDiv;
      if (labelDiv) {
        labelDiv.style.pointerEvents = selectable && areasSelectable ? 'auto' : 'none';
        labelDiv.style.cursor = selectable && areasSelectable ? 'pointer' : 'default';
      }
    });
  }, [map, selectable, areasSelectable]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up polygons
      polygonsRef.current.forEach(polygon => {
        if (polygon) {
          // Clear all event listeners
          google.maps.event.clearInstanceListeners(polygon);
          // Remove from map
          polygon.setMap(null);
        }
      });

      // Clean up region labels
      regionLabelsRef.current.forEach(overlay => {
        if (overlay) {
          overlay.setMap(null);
        }
      });

      // Clean up area labels
      areaLabelsRef.current.forEach(overlay => {
        if (overlay) {
          overlay.setMap(null);
        }
      });

      // Clean up markers
      markersRef.current.forEach(marker => {
        if (marker) {
          google.maps.event.clearInstanceListeners(marker);
          marker.setMap(null);
        }
      });
      
      // Reset refs
      polygonsRef.current = [];
      regionLabelsRef.current = [];
      areaLabelsRef.current = [];
      markersRef.current = [];
      polygonsLoadedRef.current = false;
      selectedPolygonRef.current = null;
      
    };
  }, []);

  // Update lodging markers when lodgings change
  const updateLodgingMarkers = useCallback((lodgingList) => {
    if (!map) return;

    // Clear existing lodging markers
    lodgingMarkersRef.current.forEach(marker => {
      marker.setMap(null);
    });
    lodgingMarkersRef.current = [];

    // Add new markers for lodgings
    lodgingList.forEach(lodging => {
      if (lodging.location && lodging.location.lat && lodging.location.lng) {
        const marker = new google.maps.Marker({
          position: { lat: lodging.location.lat, lng: lodging.location.lng },
          map: map,
          title: lodging.name,
          icon: {
            url: '/favicon-32x32.png', // Using the leaf icon
            scaledSize: new google.maps.Size(24, 24),
            anchor: new google.maps.Point(12, 12)
          },
          zIndex: 1000
        });

        // Add click handler for lodging marker
        marker.addListener('click', () => {
          if (onSelect) {
            onSelect('lodging', lodging);
          }
        });

        lodgingMarkersRef.current.push(marker);
      }
    });
  }, [map, onSelect]);

  // Update lodging markers when lodgings prop changes
  useEffect(() => {
    updateLodgingMarkers(lodgings);
  }, [lodgings, updateLodgingMarkers]);

  // Update selected lodging marker
  useEffect(() => {
    if (!map) return;

    // Clear existing selected lodging marker
    if (selectedLodgingMarkerRef.current) {
      selectedLodgingMarkerRef.current.setMap(null);
      selectedLodgingMarkerRef.current = null;
    }

    // Add marker for selected lodging
    if (selectedLodging && selectedLodging.location && selectedLodging.location.lat && selectedLodging.location.lng) {
      const marker = new google.maps.Marker({
        position: { lat: selectedLodging.location.lat, lng: selectedLodging.location.lng },
        map: map,
        title: selectedLodging.name,
        icon: {
          url: '/favicon-32x32.png', // Using the leaf icon
          scaledSize: new google.maps.Size(32, 32), // Larger for selected
          anchor: new google.maps.Point(16, 16)
        },
        zIndex: 2000 // Higher z-index to appear above other markers
      });

      selectedLodgingMarkerRef.current = marker;

      // Zoom to selected lodging
      map.setCenter({ lat: selectedLodging.location.lat, lng: selectedLodging.location.lng });
      map.setZoom(15);
    }
  }, [map, selectedLodging]);

  return (
    <div 
      ref={mapRef} 
      className={`w-full h-full ${className}`}
      style={{ minHeight: '400px' }}
    />
  );
};

export default RegionsMapLite;
